import { useEffect, useRef, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import { uploadMedia } from "../uploadMedia";
import { normalizeMediaUrl } from "@/lib/mediaUrl";
import type { HeroSlideItem } from "@/types/content";

const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
const labelCls = "block text-xs font-medium text-foreground-600 mb-1";
const smallBtnCls = "text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer";

function newSlide(base?: Partial<HeroSlideItem>): HeroSlideItem {
  return {
    id: `slide-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    imagen_url: "",
    imagen_alt: "",
    titulo: "",
    descripcion: "",
    ...base,
  };
}

export default function AdminBannerPrincipal() {
  const [slides, setSlides] = useState<HeroSlideItem[]>([]);
  const [currentBanner, setCurrentBanner] = useState<Partial<HeroSlideItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/bootstrap`)
      .then((res) => res.json())
      .then((json) => {
        setSlides(json.contentBlocks?.home?.hero_slides ?? []);
        const home = json.pages?.home;
        if (home) {
          setCurrentBanner({
            imagen_url: home.hero_imagen_url,
            imagen_alt: home.hero_imagen_alt,
            titulo: home.hero_titulo,
            descripcion: home.hero_descripcion,
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  function update(id: string, patch: Partial<HeroSlideItem>) {
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function move(index: number, dir: -1 | 1) {
    setSlides((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function handleFile(file: File) {
    const id = uploadTarget.current;
    if (!id) return;
    setUploadingId(id);
    setMessage(null);
    try {
      const slide = slides.find((s) => s.id === id);
      const { url } = await uploadMedia(file, slide?.titulo || "Banner principal");
      update(id, { imagen_url: url });
    } catch (err) {
      setMessage({ type: "error", text: err instanceof Error ? err.message : "No se pudo subir la imagen" });
    } finally {
      setUploadingId(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSave() {
    setMessage(null);
    if (slides.some((s) => !s.imagen_url.trim() || !s.titulo.trim())) {
      setMessage({ type: "error", text: "Cada imagen necesita su foto y su texto principal." });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/content-blocks/home/hero_slides`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: slides }),
      });
      if (!res.ok) {
        setMessage({ type: "error", text: "No se pudo guardar" });
        return;
      }
      setMessage({ type: "success", text: "Banner guardado. Los cambios ya se ven en la página de inicio." });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Banner Principal</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Imágenes que rotan en la parte de arriba de la página de inicio, cada una con su texto principal y su texto
        secundario. Con una sola imagen no hay rotación. Si no agregas ninguna, el sitio muestra el banner actual
        (el de Páginas → Home).
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />

      {message && (
        <div
          className={`mt-4 rounded-md p-3 text-sm border ${
            message.type === "success"
              ? "bg-primary-100 border-primary-200 text-primary-800"
              : "bg-accent-100 border-accent-200 text-accent-800"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mt-6 space-y-4">
        {slides.map((slide, i) => (
          <div key={slide.id} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-foreground-500">Imagen {i + 1}</span>
              <div className="flex gap-3">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className={`${smallBtnCls} disabled:opacity-30`}>
                  ↑ Subir
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === slides.length - 1}
                  className={`${smallBtnCls} disabled:opacity-30`}
                >
                  ↓ Bajar
                </button>
                <button
                  type="button"
                  onClick={() => setSlides((prev) => prev.filter((s) => s.id !== slide.id))}
                  className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer"
                >
                  Eliminar
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className={labelCls}>Foto</label>
                {slide.imagen_url ? (
                  <img src={normalizeMediaUrl(slide.imagen_url)} alt="" className="mb-2 w-full h-40 object-cover rounded-md" />
                ) : (
                  <div className="mb-2 w-full h-40 rounded-md bg-background-200 flex items-center justify-center text-xs text-foreground-500">
                    Sin foto todavía
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    className={inputCls}
                    placeholder="URL de la imagen"
                    value={slide.imagen_url}
                    onChange={(e) => update(slide.id, { imagen_url: e.target.value })}
                  />
                  <button
                    type="button"
                    disabled={uploadingId === slide.id}
                    onClick={() => {
                      uploadTarget.current = slide.id;
                      fileRef.current?.click();
                    }}
                    className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer"
                  >
                    {uploadingId === slide.id ? "Subiendo..." : "Subir foto"}
                  </button>
                </div>
              </div>

              <div>
                <label className={labelCls}>Descripción de la foto (para accesibilidad, opcional)</label>
                <input className={inputCls} value={slide.imagen_alt} onChange={(e) => update(slide.id, { imagen_alt: e.target.value })} />
              </div>

              <div>
                <label className={labelCls}>Texto principal</label>
                <input className={inputCls} value={slide.titulo} onChange={(e) => update(slide.id, { titulo: e.target.value })} />
              </div>

              <div>
                <label className={labelCls}>Texto secundario</label>
                <textarea
                  className={inputCls}
                  rows={2}
                  value={slide.descripcion}
                  onChange={(e) => update(slide.id, { descripcion: e.target.value })}
                />
              </div>
            </div>
          </div>
        ))}

        {slides.length === 0 && currentBanner && (
          <button
            type="button"
            onClick={() => setSlides([newSlide(currentBanner)])}
            className="w-full rounded-lg border border-background-300 bg-background-50 py-3 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            Empezar con el banner actual como primera imagen
          </button>
        )}

        <button
          type="button"
          onClick={() => setSlides((prev) => [...prev, newSlide()])}
          className="w-full rounded-lg border-2 border-dashed border-background-300 py-3 text-sm text-foreground-500 hover:border-primary-300 hover:text-primary-600 cursor-pointer"
        >
          + Agregar imagen
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
        >
          {saving ? "Guardando..." : "Guardar banner"}
        </button>
      </div>
    </AdminLayout>
  );
}
